import React, { Fragment, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { flow, get } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connect } from 'react-redux';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { useCoreSrpFilter } from 'site-modules/shared/hooks/core-inventory/use-core-srp-filter';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { PageModel } from 'client/data/models/page';
import {
  BATTERY_RANGE_SCORE,
  BODY_TYPE,
  COMBINE_MPG,
  ELECTRICITY_RANGE,
  INTERIOR_COLOR,
  MAKE,
  MODEL,
  RADIUS,
  SORT_BY,
  TOTAL_SEATING,
} from 'site-modules/shared/constants/allowed-inventory-request-params';
import { CPO_CARS } from 'site-modules/shared/utils/cpo-message';
import { expandedRadiusDueToLowResultsSort } from 'site-modules/shared/constants/sort-types';
import { getSelectedPaymentType } from 'site-modules/shared/utils/inventory-utils/get-current-payment-type';
import { transformSelectedFacetsForDisplaying } from 'site-modules/shared/utils/inventory/search-filter';
import {
  addFiltersToQuery,
  CORE_SRP_INVENTORY_COUNT,
  getInitialSearchFilter,
} from 'site-modules/core-page/utils/core-srp';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { srpLinkBuilderWithParamsConversion } from 'site-modules/shared/utils/srp-link-constructor';
import { withListTrackingData } from 'site-modules/shared/components/inventory-list-tracking/list-tracking-data';
import { withInventoryListTracking } from 'site-modules/shared/components/inventory-list-tracking/inventory-list-tracking';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { Link } from 'site-modules/shared/components/link/link';
import { Zip } from 'site-modules/shared/components/zip/zip';
import { StyledZipInput } from 'site-modules/shared/components/styled-zip-input/styled-zip-input';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { SuggestedFacets } from 'site-modules/shared/components/inventory/suggested-facets/suggested-facets';
import { QuickFilters } from 'site-modules/shared/components/inventory/quick-filters/quick-filters';
import { UsurpFilters } from 'site-modules/shared/components/inventory/usurp-filters/usurp-filters';
import { UsurpFiltersDrawer } from 'site-modules/shared/components/inventory/usurp-filters-drawer/usurp-filters-drawer';
import { UsurpInventoryCard } from 'site-modules/shared/components/inventory/usurp-inventory-card/usurp-inventory-card';
import { SrpCardsTopPanel } from 'site-modules/shared/components/inventory/srp-cards-top-panel/srp-cards-top-panel';
import { CertifiedPreOwnedMessage } from 'site-modules/shared/components/inventory/certified-pre-owned-message/certified-pre-owned-message';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './core-srp.scss';

const EXCLUDED_FILTER_TYPES = [
  BODY_TYPE,
  MAKE,
  MODEL,
  COMBINE_MPG,
  ELECTRICITY_RANGE,
  RADIUS,
  INTERIOR_COLOR,
  TOTAL_SEATING,
  BATTERY_RANGE_SCORE,
];
const CREATIVE_ID = 'edm-entry-inventory-cards';

export function fireTrackingZipChange(value) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_PROVIDE_ZIP_CODE,
      subaction_name: TrackingConstant.ACTION_PROVIDE_ZIP_CODE,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
      creative_id: CREATIVE_ID,
      value,
    },
  });
}

export function CoreSrpUI({
  vehicleParams: { makeName, modelName, makeSlug, modelSlug, year },
  className,
  inventoryData,
  selectedFacets,
  visitorLocation,
  location,
  setModelValue,
  zipCode,
  isMobile,
  isCore6829,
}) {
  const containerRef = useRef();
  const [isStickyEnabled] = useOnScreen(containerRef);

  const { city, stateCode } = visitorLocation;
  const isInternational = isUnknownZip(visitorLocation);
  const isCore6829Enabled = isInternational && isCore6829;
  const { totalNumber: inventoryCount } = get(inventoryData, 'inventories', {});
  const facets = get(inventoryData, 'facets', []).filter(({ type }) => !EXCLUDED_FILTER_TYPES.includes(type));
  const displayInventories =
    get(inventoryData, 'inventories.results') &&
    inventoryData.inventories.results.slice(0, CORE_SRP_INVENTORY_COUNT[isMobile]);
  const suggestedFacets = get(inventoryData, 'suggestedFacets', []);
  const isExpandedRadiusDueToLowResults = !!get(inventoryData, 'attributes.expandedRadiusDueToLowResults', false);
  const attrRadius = get(inventoryData, 'attributes.radius');
  const isExpandedRadiusDueToLowResultsSort = isExpandedRadiusDueToLowResults && attrRadius === 6000;

  const initialFilter = useMemo(() => getInitialSearchFilter({ makeSlug, modelSlug, year }), [
    makeSlug,
    modelSlug,
    year,
  ]);

  const {
    filteringByFacets,
    radius,
    onUpdateFilter,
    onFiltersButtonClick,
    closeFiltersDrawer,
    cancelFiltersUpdate,
    updateZipCode,
    isFilterDrawerOpen,
    isLoading,
  } = useCoreSrpFilter({
    inventoryData,
    facets,
    selectedFacets,
    initialFilter,
    pathname: location.pathname,
    visitorLocation,
    addFiltersToQuery,
    setModelValue,
  });

  const renderSuggestedFacetsComponent = useCallback(
    suggestedFacetsToRender => (
      <SuggestedFacets
        suggestedFacets={suggestedFacetsToRender.filter(({ type }) => !EXCLUDED_FILTER_TYPES.includes(type))}
        onSuggestedFacetClick={onUpdateFilter}
        selectedFacets={selectedFacets}
      />
    ),
    [onUpdateFilter, selectedFacets]
  );

  const renderFilters = useCallback(
    sticky => (
      <div className={classnames({ 'filters-container rounded-12 bg-white px-0_25 py-0_5 mb-1_5': !sticky })}>
        <QuickFilters
          filtersButtonClassName=""
          listClassName="px-0_25 py-0_25 pb-lg-0_25"
          suggestedFacets={suggestedFacets}
          facets={facets}
          sticky={sticky}
          activeFiltersCount={transformSelectedFacetsForDisplaying(filteringByFacets).length}
          onFiltersBtnClick={onFiltersButtonClick}
          withFiltersBtn
          renderSuggestedFacetsComponent={renderSuggestedFacetsComponent}
        />
      </div>
    ),
    [facets, filteringByFacets, onFiltersButtonClick, renderSuggestedFacetsComponent, suggestedFacets]
  );

  if (!inventoryData) {
    return null;
  }

  const h2Ctrl = <h2 className="heading-3 mb-0_5">For sale {isCore6829Enabled ? 'nationwide' : 'nearby'}</h2>;

  return (
    <div
      ref={containerRef}
      className={classnames('core-srp bg-cool-gray-90 px-0_5 px-md-1 py-1_5', className, { 'rounded-12': !isMobile })}
      data-tracking-parent={CREATIVE_ID}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-1">
        {isInternational ? (
          h2Ctrl
        ) : (
          <Experiment name="core-6835-used-nozip" showDefault>
            <Recipe name="ctrl" isDefault>
              {h2Ctrl}
            </Recipe>
            <Recipe name="chal">
              <div className="mb-0_5">
                <h2 className="heading-3 d-inline me-0_5">
                  For sale near {city}, {stateCode}
                </h2>{' '}
                <Zip
                  zip={zipCode}
                  onUpdate={updateZipCode}
                  buttonClassName="size-16 text-blue-40 fw-normal text-transform-none"
                  buttonText="Change location"
                />
              </div>
            </Recipe>
          </Experiment>
        )}
        {isCore6829Enabled && (
          <StyledZipInput
            styledWrapperClasses="mb-0_5"
            wrapperClasses="zip-input-wrapper rounded-8"
            onValidate={validation.validateZip}
            fireTrackingZipChange={fireTrackingZipChange}
            onZipChange={updateZipCode}
            placeholder="Enter ZIP"
            btnClassname="fw-medium btn-blue-50"
            btnTrackingId="provide_zip_code"
            inputClasses="medium rounded-8"
            errorTextClasses="pos-a"
            goText="See local listings"
            forceEmptyZip
            noZipFromStore
            withGoBtn
            noUpdateOnBlur
            withErrorText
          />
        )}
      </div>
      <LoadingSpinner isLoading={isLoading}>
        <AnimatedSticky
          stickyClassName="py-0_5 px-1 px-md-1_75"
          stickyFragment={renderFilters}
          hideSticky={!inventoryCount || !isStickyEnabled}
        >
          {renderFilters()}
        </AnimatedSticky>

        {!!inventoryCount && (
          <Fragment>
            <SrpCardsTopPanel
              onUpdate={onUpdateFilter}
              inventoryCount={inventoryCount}
              isMobile={isMobile}
              withRange={!!facets.find(({ type }) => type === ELECTRICITY_RANGE)}
              selectedFacets={
                !get(selectedFacets, 'sortBy') && isExpandedRadiusDueToLowResultsSort
                  ? { ...selectedFacets, [SORT_BY]: [expandedRadiusDueToLowResultsSort] }
                  : selectedFacets
              }
            />
            {!!CPO_CARS[makeSlug]?.[modelSlug]?.[year] && (
              <CertifiedPreOwnedMessage make={makeSlug} isMobile={isMobile} />
            )}
            <Row tag="ul" className="list-unstyled mb-0_5" data-tracking-parent="edm-entry-vehicle-card-list">
              {displayInventories &&
                displayInventories.map(inventory => (
                  <Col key={inventory.vin} tag="li" xs={12} md={6} className="mb-1_5">
                    <UsurpInventoryCard vehicle={inventory} radius={radius} lazyLoadImage isVinPreviewLink />
                  </Col>
                ))}
            </Row>
          </Fragment>
        )}

        {!inventoryCount && (
          <div className="mb-2">
            Sorry, there are no {year} {makeName} {modelName} vehicles for sale in our network.
          </div>
        )}

        <div className="text-center">
          <Link
            to={srpLinkBuilderWithParamsConversion(selectedFacets)}
            rel="nofollow"
            className="text-blue-40"
            data-tracking-id="view_search_result"
          >
            See all for sale
            <i className="icon-arrow-right3 d-inline-block size-10 ms-0_25" aria-hidden />
          </Link>
        </div>
      </LoadingSpinner>

      <UsurpFiltersDrawer
        isOpen={isFilterDrawerOpen}
        onToggle={closeFiltersDrawer}
        onCancel={cancelFiltersUpdate}
        onApply={closeFiltersDrawer}
        searchResultsFound={inventoryCount}
        isLoading={isLoading}
        dataTrackingParent="srp-new-drawer-facets"
        drawerContainerClasses="core-srp-filters-drawer"
        isMobile={isMobile}
        filtersComponent={UsurpFilters}
        isNational={isCore6829Enabled}
        paymentType={getSelectedPaymentType(selectedFacets)}
        facets={facets}
        filteringByFacets={filteringByFacets}
        onUpdate={onUpdateFilter}
        zipCode={zipCode}
        onZipCodeUpdate={updateZipCode}
        selectedFacets={selectedFacets}
      />
    </div>
  );
}

CoreSrpUI.propTypes = {
  vehicleParams: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    year: PropTypes.string,
  }).isRequired,
  setModelValue: PropTypes.func.isRequired,
  className: PropTypes.string,
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
  }),
  visitorLocation: VisitorEntities.Location,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  zipCode: PropTypes.string,
  selectedFacets: PropTypes.shape({}),
  isMobile: PropTypes.bool,
  isCore6829: PropTypes.bool,
};

CoreSrpUI.defaultProps = {
  className: null,
  inventoryData: null,
  visitorLocation: null,
  location: null,
  zipCode: null,
  selectedFacets: null,
  isMobile: false,
  isCore6829: false,
};

const mapStateToProps = state => ({
  vehicle: state.pageContext.vehicle,
  isCore6829:
    ExperimentUtil.getForcedOrAssignedRecipeName({
      state,
      campaignName: 'core-6829-used-nozip',
      defaultVal: 'ctrl',
    }) === 'chal',
});

export const propsAreEqual = (prevProps, { inventoryData }) => !inventoryData;
export const CoreSrp = flow(
  connect(mapStateToProps),
  withInventoryListTracking,
  withListTrackingData,
  component =>
    connectToModel(component, {
      zipCode: bindToPath('location.zipCode', VisitorModel),
      visitorLocation: bindToPath('location', VisitorModel, null, false),
      location: bindToPath('location', PageModel),
      inventoryData: bindToPath('searchResults', InventoryModel, null, false),
      selectedFacets: bindToPath('searchResultsFilter', InventoryModel, null, false),
    })
)(React.memo(CoreSrpUI, propsAreEqual));
